import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChartLine, PaperPlaneTilt, CircleNotch, ArrowLeft, CheckCircle } from 'phosphor-react';
import { useAuth } from '../context/AuthContext';

export default function Support() {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!email || !validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate message
    if (!message || message.trim().length === 0) {
      setError('Please enter your message');
      return;
    }

    setLoading(true);

    try {
      await api.post('/support', {
        email: email.trim(),
        message: message.trim()
      });

      setSuccess(true);
      setMessage('');
      toast.success('Your message has been sent successfully');
    } catch (err) {
      console.error('Support request error:', err);
      setError('Failed to send message. Please try again.');
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = () => {
    setSuccess(false);
    setError('');
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8">
      <div className="w-full max-w-xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2">
            Support
          </h2>
          <p className="text-white/60 text-base font-sans">
            Have a question or need help? Send us a message.
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
            <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            <h3 className="text-2xl font-bold font-heading text-white mb-2">Message Sent!</h3>
            <p className="text-white/60 mb-6">
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <Button
              onClick={handleNewMessage}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="send-another-btn"
            >
              Send Another Message
            </Button>
          </div>
        ) : (
          /* Form */
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-white/80 mb-2 block">
                  Your Email <span className="text-[#FF3B30]">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="support-email"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-white/80 mb-2 block">
                  Message <span className="text-[#FF3B30]">*</span>
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue or question..."
                  required
                  disabled={loading}
                  rows={6}
                  className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                  data-testid="support-message"
                />
              </div>

              {error && (
                <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3" data-testid="support-error">
                  <p className="text-[#FF3B30] text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="support-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <CircleNotch size={18} className="animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <PaperPlaneTilt size={18} weight="bold" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
